'use client';

import { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faHiveIconSmallLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import FavoritesModalImage from '@/app/_components/FavoritesModalImage';
import FavoritesListSelect from '@/app/_components/FavoritesListSelect';
import SubmitWithPendingButton from '@/app/_components/SubmitWithPendingButton';
import updateFavoritesListAction from '@/app/_lib/actions/UpdateFavoritesListAction';
import { useGlobalMessage } from '@/app/_lib/hooks';
import { withMinDuration } from '@/app/_lib/services/ClientUtil';
const DeleteFavoritesListModal = ({
  isOpen,
  setIsOpen,
  contentLabel,
  favoritesList,
  favoritesListIndex,
  allFavoritesLists = [],
  onConfirmDelete,
  onListRenamed
}) => {
  useEffect(() => {
    Modal.setAppElement('#hivenext');
  }, []);
  const addGlobalMessage = useGlobalMessage();
  const [listName, setListName] = useState('');
  const [targetListIndex, setTargetListIndex] = useState(null);
  const [selectedMoveOption, setSelectedMoveOption] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  useEffect(() => {
    if (favoritesList) {
      setListName(favoritesList.list_name || '');
    }
  }, [favoritesList]);
  const productCount = favoritesList?.favorites?.length || 0;
  const hasProducts = productCount > 0;
  const otherLists = allFavoritesLists.filter(list => list.list_index !== favoritesListIndex);

  // Build options for CustomSelect
  const moveOptions = [{
    value: '',
    label: "Don't move items (delete them)",
    display_order: 0
  }, ...otherLists.map(list_0 => ({
    value: list_0.list_index,
    label: list_0.list_name,
    display_order: list_0.list_index + 1
  }))];
  const afterOpenModal = () => {
    setErrorMessage('');
    setTargetListIndex(null);
    setSelectedMoveOption(null);
    if (favoritesList) {
      setListName(favoritesList.list_name || '');
    }
  };
  const closeModal = () => {
    setIsOpen(false);
  };
  const handleMoveOptionSelect = option => {
    setSelectedMoveOption(option);
    setTargetListIndex(option.value !== '' ? option.value : null);
  };
  const handleRenameSubmit = useCallback(async formData => {
    const newListName = formData.get('list_name');
    if (!newListName || !newListName.trim()) {
      setErrorMessage('Please enter a list name.');
      return;
    }
    setErrorMessage('');
    const data = {
      list_index: favoritesListIndex,
      list_name: newListName.trim()
    };
    const response = await withMinDuration(updateFavoritesListAction(data), 700);
    if (response.success) {
      addGlobalMessage({
        message: 'List renamed successfully.',
        messageType: 'success'
      });
      if (onListRenamed) {
        onListRenamed(newListName.trim());
      }
      closeModal();
    } else {
      setErrorMessage(response.message || 'An error occurred. Please try again.');
    }
  }, [favoritesListIndex, addGlobalMessage, onListRenamed, closeModal]);
  const handleDeleteClick = () => {
    if (onConfirmDelete) {
      onConfirmDelete(targetListIndex);
    }
    closeModal();
  };
  return <Modal overlayClassName="z-[1055] fixed outline-0 top-0 bottom-0 left-0 right-0 bg-modal-overlay" className="fixed top-1/2 left-1/2 right-auto bottom-auto -mr-[50%] -translate-y-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-[500px] md:max-w-[1000px] rounded-sm bg-white shadow-2xl outline-none" isOpen={isOpen} onAfterOpen={afterOpenModal} onRequestClose={closeModal} contentLabel={contentLabel} closeTimeoutMS={300} bodyOpenClassName="ReactModal__Body--open" htmlOpenClassName="ReactModal__Html--open">
            <div className="flex flex-col md:flex-row">
                <FavoritesModalImage alt="Delete favorites list" />

                {/* Right Side - Content */}
                <div className="w-full md:w-1/2 px-6 md:px-8 py-10 md:py-14 pb-2 md:pb-3 relative flex flex-col justify-between order-1 md:order-2">
                    {/* Top Section - Content */}
                    <div>
                        {/* Close Button */}
                        <button onClick={closeModal} className="absolute top-4 right-4 text-light hover:text-md transition-colors" aria-label="Close">
                            <FontAwesomeIcon icon={faXmark} className="text-[28px]" />
                        </button>

                        {/* Heading with Hive Icon */}
                        <div className="flex items-center gap-3 mb-3">
                            <FontAwesomeIcon icon={faHiveIconSmallLight} className="text-2xl text-[#F5A623]" />
                            <h2 className="text-2xl md:text-3xl font-extralight">
                                Delete List
                            </h2>
                        </div>

                        {/* Item Count Message */}
                        {hasProducts && <div className="text-base text-light mb-4">
                                <span className="text-light font-medium">'{favoritesList?.list_name}'</span> contains {productCount} item{productCount !== 1 ? 's' : ''}. Would you like to move to another list?
                            </div>}

                        {errorMessage && <p className="text-sm text-red-600 mb-4">{errorMessage}</p>}

                        {/* Move Products Option */}
                        {hasProducts && otherLists.length > 0 && <div className="mb-6">
                                <FavoritesListSelect identifier="move-to-list" label="Select A List To Move To" options={moveOptions} selectedOption={selectedMoveOption} onSelect={handleMoveOptionSelect} showHeartOnSelected={false} className="w-full" />
                            </div>}

                        {/* Rename Option */}
                        <p className="text-base text-md mb-3">
                            Rename this list instead.
                        </p>

                        <form action={handleRenameSubmit}>
                            <div className="flex items-center gap-2">
                                <div className="form-group flex-1">
                                    <input id="rename_list" type="text" name="list_name" value={listName} onChange={e => setListName(e.target.value)} autoComplete="off" placeholder=" " required className="form-input form-input-sm peer" />
                                    <label htmlFor="rename_list" className="form-input-label form-input-label-sm peer-placeholder-shown:form-input-label-placeholder-shown">List Name</label>
                                </div>
                                <SubmitWithPendingButton pendingText="Saving..." size="sm">
                                    Rename
                                </SubmitWithPendingButton>
                            </div>
                        </form>

                        {/* Delete Section */}
                        <p className="text-base text-md mb-0 mt-6">
                            Are you sure you want to delete this list?
                        </p>
                        <p className="text-xs text-light leading-relaxed italic">
                            Deleting a list is permanent and cannot be undone.
                        </p>
                        <div className="flex items-center gap-4 mb-6">
                            <button type="button" onClick={handleDeleteClick} className="btn btn-primary btn-sm w-auto">
                                Delete List
                            </button>

                            <button type="button" onClick={closeModal} className="text-xs text-light hover:text-dark transition-colors underline">
                                Cancel
                            </button>
                        </div>
                    </div>

                    
                </div>
            </div>
        </Modal>;
};
export default DeleteFavoritesListModal;